import { useCallback, useEffect, useState } from 'react';
import { SimpleSelect, getBootstrapData } from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';
import FeatureGateNotice from '@/hub-shared/components/FeatureGateNotice';
import {
  allowGate,
  complianceSubscriptionRequiredGate,
  type FeatureGateDecision,
} from '@/hub-shared/gates/featureGateDecisions';
import { AddItemIcon } from '../../../companies/src/shared/AddItemIcon';
import CabinetPrefillSettingsSection from './CabinetPrefillSettingsSection';
import DiligencePackEditorModal from './DiligencePackEditorModal';
import type { DiligencePackApiRow } from './DiligencePackEditorModal';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';
import { errorToUserFacingMessage } from '../api/apiErrors';
import type { PortalBootstrapData } from '../types/window';
import { getCabinetManageControlLevelGate, getPortalGate } from '../gates/portalGates';

type CabinetComplianceTabProps = {
  cabinetId: number;
  bootstrap: PortalBootstrapData;
  bootstrapKey: string;
  onSessionExpired: () => void;
};

class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export const getVsmeApiConfig = () => {
  const bs = getBootstrapData('AccountingHubPortal') as { restUrlVsme?: string; nonce: string };
  const base = (bs.restUrlVsme ?? '').replace(/\/?$/, '/');
  return { base, nonce: bs.nonce };
};

export const vsmeRequest = async <T,>(method: string, path: string, body?: unknown): Promise<T> => {
  const { base, nonce } = getVsmeApiConfig();
  const res = await fetch(`${base}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': nonce },
    credentials: 'same-origin',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    throw new HttpError(await parseApiError(res), res.status);
  }
  return res.json() as Promise<T>;
};

type DossierAttributionSettings = {
  default_scope_mode: 'all' | 'explicit';
  role_overrides: {
    expert_comptable: ExpertComptableAttributionPayload;
  };
};

type MissionPolicySettings = {
  require_letter_of_mission: boolean;
  require_acceptance_questionnaire: boolean;
  require_aml_questionnaire: boolean;
  report_generation_readiness_mode: 'strict' | 'standard' | 'permissive';
};

type ExpertComptableAttributionPayload = {
  mode: 'all' | 'via_role';
  via_role_slug: string | null;
};

type SettingsState = 'loading' | 'ready' | 'saving' | 'error' | 'forbidden';

const DEFAULT_SCOPE_OPTIONS = () => [
  { value: 'all', label: __('Tous les dossiers clients') },
  { value: 'explicit', label: __('Attribution manuelle') },
];

const EXPERT_SCOPE_OPTIONS = () => [
  { value: 'all', label: __('Tous les dossiers clients') },
  {
    value: 'via_role',
    label: __('Dossiers des collaborateurs reliés à l\'expert'),
  },
];

const REPORT_GENERATION_READINESS_OPTIONS = () => [
  {
    value: 'permissive',
    label: __('Permissive — aucune restrictions'),
  },
  {
    value: 'standard',
    label: __('Standard — formulaire complété'),
  },
  {
    value: 'strict',
    label: __('Stricte — toutes diligences supervisées'),
  },
];

const isReadinessModeValue = (
  value: string,
): value is MissionPolicySettings['report_generation_readiness_mode'] =>
  value === 'strict' || value === 'standard' || value === 'permissive';

const MANUAL_SCOPE_WARNING = __(
  'Passer en attribution manuelle va retirer les dossiers actuellement attribués par défaut. Vous pourrez encore régler le comportement des experts-comptables avant d\'enregistrer.',
);

const PACKS_ACCESS_FORBIDDEN_FALLBACK = __(
  'Impossible d’afficher les packs de diligences : les droits de ce compte sur ce cabinet ne suffisent pas. Demandez une révision de vos habilitations au propriétaire du cabinet ou à votre administrateur Plateforme.',
);

const WORDPRESS_COLLABORATEUR_SLUG = 'collaborateur_comptable';

const normalizeExpertComptableFromApi = (raw: unknown): ExpertComptableAttributionPayload => {
  if (typeof raw === 'string') {
    if (raw === 'all') {
      return { mode: 'all', via_role_slug: null };
    }
    if (raw === 'via_role') {
      return { mode: 'via_role', via_role_slug: WORDPRESS_COLLABORATEUR_SLUG };
    }
    if (raw === 'inherit') {
      return { mode: 'via_role', via_role_slug: WORDPRESS_COLLABORATEUR_SLUG };
    }
    return { mode: 'via_role', via_role_slug: WORDPRESS_COLLABORATEUR_SLUG };
  }
  if (raw !== null && typeof raw === 'object' && 'mode' in raw) {
    const o = raw as { mode?: unknown; via_role_slug?: unknown };
    const m = typeof o.mode === 'string' ? o.mode : '';
    const slug =
      typeof o.via_role_slug === 'string' && o.via_role_slug !== ''
        ? o.via_role_slug
        : null;
    if (m === 'all') {
      return { mode: 'all', via_role_slug: null };
    }
    if (m === 'via_role') {
      return { mode: 'via_role', via_role_slug: slug ?? WORDPRESS_COLLABORATEUR_SLUG };
    }
    if (m === 'inherit') {
      return { mode: 'via_role', via_role_slug: slug ?? WORDPRESS_COLLABORATEUR_SLUG };
    }
  }
  return { mode: 'via_role', via_role_slug: WORDPRESS_COLLABORATEUR_SLUG };
};

const sanitizeLoadedDossierSettings = (
  incoming: DossierAttributionSettings,
): DossierAttributionSettings => ({
  ...incoming,
  role_overrides: {
    expert_comptable: normalizeExpertComptableFromApi(incoming.role_overrides?.expert_comptable),
  },
});

const expertAttributionEquals = (
  a: ExpertComptableAttributionPayload,
  b: ExpertComptableAttributionPayload,
): boolean =>
  a.mode === b.mode
  && (a.mode !== 'via_role'
    ? true
    : (a.via_role_slug ?? WORDPRESS_COLLABORATEUR_SLUG) === (b.via_role_slug ?? WORDPRESS_COLLABORATEUR_SLUG));

type ModalMode =
  | { kind: 'create' }
  | { kind: 'edit'; pack: DiligencePackApiRow };

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArchiveIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polyline points="21 8 21 21 3 21 3 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="1" y="3" width="22" height="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="10" y1="12" x2="14" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type DiligencePackCardProps = {
  pack: DiligencePackApiRow;
  onEdit: () => void;
  onArchive: () => void;
  disabled?: boolean;
  disabledReason?: string;
};

const DiligencePackCard = ({ pack, onEdit, onArchive, disabled = false, disabledReason }: DiligencePackCardProps) => (
  <div className="container-style pack-card">
    <div className="pack-card__actions">
    <span className={`pack-card__mode pack-card__mode--${pack.target_mode}`}>
      {pack.target_mode === 'all' ? __('Tous les dossiers') : __('Dossiers sélectionnés')}
    </span>
      <button
        type="button"
        className="btn-icon pack-card__action-btn"
        onClick={onEdit}
        aria-label={__('Modifier le pack')}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
      >
        <PencilIcon />
      </button>
      <button
        type="button"
        className="btn-icon pack-card__action-btn pack-card__action-btn--danger"
        onClick={onArchive}
        aria-label={__('Archiver le pack')}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
      >
        <ArchiveIcon />
      </button>
    </div>
    <p className="pack-card__name">{pack.name}</p>
    {pack.description !== null && pack.description !== '' && (
      <p className="pack-card__description">{pack.description}</p>
    )}

  </div>
);

const firstDeniedGate = (...gates: FeatureGateDecision[]): FeatureGateDecision => {
  for (const gate of gates) {
    if (!gate.allowed) {
      return gate;
    }
  }
  return allowGate();
};

const ATTRIBUTION_READ_FORBIDDEN_MESSAGE = __(
  'Impossible d’afficher les paramètres d’attribution : les droits de ce compte sur ce cabinet ne suffisent pas.',
);

const MISSION_POLICY_READ_FORBIDDEN_MESSAGE = __(
  'Impossible d’afficher la politique de mission : les droits de ce compte sur ce cabinet ne suffisent pas.',
);

const CabinetComplianceTab = ({ cabinetId, bootstrap, bootstrapKey, onSessionExpired }: CabinetComplianceTabProps) => {
  const [packs, setPacks] = useState<DiligencePackApiRow[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error' | 'forbidden'>('loading');
  const [loadError, setLoadError] = useState('');
  const [packsForbiddenDetail, setPacksForbiddenDetail] = useState('');
  const [modal, setModal] = useState<ModalMode | null>(null);
  const [settings, setSettings] = useState<DossierAttributionSettings | null>(null);
  const [draftSettings, setDraftSettings] = useState<DossierAttributionSettings | null>(null);
  const [settingsState, setSettingsState] = useState<SettingsState>('loading');
  const [settingsError, setSettingsError] = useState('');
  const [missionPolicy, setMissionPolicy] = useState<MissionPolicySettings | null>(null);
  const [draftMissionPolicy, setDraftMissionPolicy] = useState<MissionPolicySettings | null>(null);
  const [missionPolicyState, setMissionPolicyState] = useState<SettingsState>('loading');
  const [missionPolicyError, setMissionPolicyError] = useState('');
  const [manualWarningOpen, setManualWarningOpen] = useState(false);
  const [manualWarningAcknowledged, setManualWarningAcknowledged] = useState(false);
  const cabinetScope =
    bootstrap.cabinetId !== null && bootstrap.cabinetId !== undefined && bootstrap.cabinetId > 0;
  const roles = bootstrap.currentUser?.roles ?? [];
  const canEditAttribution = cabinetScope && !roles.includes('utilisateur_entreprise');
  const canUseCustomDiligencePacks =
    bootstrap.capabilities.manageOptions === true
    || bootstrap.entitlements?.features['diligence.mode'] === 'complete';
  const withComplianceSubscriptionMessage = (gate: FeatureGateDecision): FeatureGateDecision => {
    if (gate.allowed || gate.reason !== 'subscription_required') {
      return gate;
    }
    return complianceSubscriptionRequiredGate(bootstrap.subscriptionTierUrl);
  };
  const manageControlLevelGate = getCabinetManageControlLevelGate(bootstrap);
  const dossierAttributionEditGate = firstDeniedGate(
    withComplianceSubscriptionMessage(getPortalGate(bootstrap, 'cabinet.compliance.dossier_attribution')),
    manageControlLevelGate,
  );
  const missionPolicyEditGate = firstDeniedGate(
    withComplianceSubscriptionMessage(getPortalGate(bootstrap, 'cabinet.compliance.mission_policy')),
    manageControlLevelGate,
  );
  const diligencePacksSubscriptionGate = canUseCustomDiligencePacks
    ? allowGate()
    : complianceSubscriptionRequiredGate(bootstrap.subscriptionTierUrl);
  const diligencePacksEditGate = firstDeniedGate(
    diligencePacksSubscriptionGate,
    manageControlLevelGate,
  );
  const attributionControlsDisabled =
    !canEditAttribution || settingsState === 'saving' || !dossierAttributionEditGate.allowed;
  const missionPolicyControlsDisabled =
    missionPolicyState === 'saving' || !missionPolicyEditGate.allowed;

  const hubRequest = useCallback(async <T,>(method: string, path: string, body?: unknown): Promise<T> => {
    const response = await fetchWithNonce(`${bootstrap.restUrls.accountingHub}${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': bootstrap.nonce,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      throw new HttpError(await parseApiError(response), response.status);
    }

    return response.json() as Promise<T>;
  }, [bootstrap.nonce, bootstrap.restUrls.accountingHub]);

  const fetchPacks = useCallback(async () => {
    setLoadState('loading');
    setPacksForbiddenDetail('');
    try {
      const res = await vsmeRequest<{ success: boolean; data: { packs: DiligencePackApiRow[] } }>(
        'GET',
        `cabinets/${cabinetId}/diligence-packs`,
      );
      setPacks(res.data?.packs ?? []);
      setLoadState('ready');
    } catch (e) {
      if (e instanceof HttpError && e.status === 403) {
        const trimmed = e.message.trim();
        setPacksForbiddenDetail(
          trimmed !== '' && !trimmed.startsWith('HTTP ')
            ? trimmed
            : '',
        );
        setLoadState('forbidden');
        return;
      }

      setLoadError(errorToUserFacingMessage(e, __('Chargement impossible.')));
      setLoadState('error');
    }
  }, [cabinetId]);

  useEffect(() => {
    void fetchPacks();
  }, [fetchPacks]);

  const fetchSettings = useCallback(async () => {
    setSettingsState('loading');
    try {
      const data = await hubRequest<DossierAttributionSettings>('GET', 'cabinet/dossier-attribution-settings');
      const ready = sanitizeLoadedDossierSettings(data);
      setSettings(ready);
      setDraftSettings(ready);
      setManualWarningAcknowledged(false);
      setManualWarningOpen(false);
      setSettingsState('ready');
    } catch (e) {
      if (e instanceof HttpError && e.status === 403) {
        setSettingsState('forbidden');
        return;
      }

      setSettingsError(errorToUserFacingMessage(e, __('Chargement impossible.')));
      setSettingsState('error');
    }
  }, [hubRequest]);

  useEffect(() => {
    void fetchSettings();
  }, [fetchSettings]);

  const fetchMissionPolicy = useCallback(async () => {
    setMissionPolicyState('loading');
    try {
      const data = await hubRequest<MissionPolicySettings>('GET', 'cabinet/mission-policy');
      setMissionPolicy(data);
      setDraftMissionPolicy(data);
      setMissionPolicyState('ready');
    } catch (e) {
      if (e instanceof HttpError && e.status === 403) {
        setMissionPolicyState('forbidden');
        return;
      }

      setMissionPolicyError(errorToUserFacingMessage(e, __('Chargement impossible.')));
      setMissionPolicyState('error');
    }
  }, [hubRequest]);

  useEffect(() => {
    void fetchMissionPolicy();
  }, [fetchMissionPolicy]);

  const saveSettings = async () => {
    if (draftSettings === null) {
      return;
    }

    setSettingsState('saving');
    setSettingsError('');

    try {
      const data = await hubRequest<DossierAttributionSettings>(
        'PATCH',
        'cabinet/dossier-attribution-settings',
        draftSettings,
      );
      const ready = sanitizeLoadedDossierSettings(data);
      setSettings(ready);
      setDraftSettings(ready);
      setManualWarningAcknowledged(false);
      setManualWarningOpen(false);
      setSettingsState('ready');
    } catch (e) {
      setSettingsError(errorToUserFacingMessage(e, __('Enregistrement impossible.')));
      setSettingsState('error');
    }
  };

  const saveMissionPolicy = async () => {
    if (draftMissionPolicy === null) {
      return;
    }

    setMissionPolicyState('saving');
    setMissionPolicyError('');

    try {
      const data = await hubRequest<MissionPolicySettings>(
        'PATCH',
        'cabinet/mission-policy',
        draftMissionPolicy,
      );
      setMissionPolicy(data);
      setDraftMissionPolicy(data);
      setMissionPolicyState('ready');
    } catch (e) {
      setMissionPolicyError(errorToUserFacingMessage(e, __('Enregistrement impossible.')));
      setMissionPolicyState('error');
    }
  };

  const handleDefaultScopeChange = (value: string) => {
    if (draftSettings === null || (value !== 'all' && value !== 'explicit')) {
      return;
    }

    setDraftSettings({
      ...draftSettings,
      default_scope_mode: value,
    });

    if (value === 'explicit' && settings?.default_scope_mode !== 'explicit') {
      setManualWarningOpen(true);
      setManualWarningAcknowledged(false);
    } else {
      setManualWarningOpen(false);
      setManualWarningAcknowledged(false);
    }
  };

  const handleExpertScopeChange = (value: string) => {
    if (draftSettings === null || (value !== 'all' && value !== 'via_role')) {
      return;
    }

    const payload: ExpertComptableAttributionPayload =
      value === 'via_role'
        ? { mode: 'via_role', via_role_slug: WORDPRESS_COLLABORATEUR_SLUG }
        : { mode: value, via_role_slug: null };

    setDraftSettings({
      ...draftSettings,
      role_overrides: {
        ...draftSettings.role_overrides,
        expert_comptable: payload,
      },
    });
  };

  const isDirty =
    settings !== null &&
    draftSettings !== null &&
    (
      settings.default_scope_mode !== draftSettings.default_scope_mode
      || !expertAttributionEquals(
        settings.role_overrides.expert_comptable,
        draftSettings.role_overrides.expert_comptable,
      )
    );

  const missionPolicyDirty =
    missionPolicy !== null
    && draftMissionPolicy !== null
    && JSON.stringify(missionPolicy) !== JSON.stringify(draftMissionPolicy);

  const setMissionPolicyFlag = (key: keyof MissionPolicySettings, checked: boolean) => {
    if (draftMissionPolicy === null) {
      return;
    }
    setDraftMissionPolicy({
      ...draftMissionPolicy,
      [key]: checked,
    });
  };

  const setMissionPolicyReadinessMode = (mode: MissionPolicySettings['report_generation_readiness_mode']) => {
    if (draftMissionPolicy === null) {
      return;
    }
    setDraftMissionPolicy({
      ...draftMissionPolicy,
      report_generation_readiness_mode: mode,
    });
  };

  const manualModePending =
    draftSettings?.default_scope_mode === 'explicit'
    && settings?.default_scope_mode !== 'explicit'
    && !manualWarningAcknowledged;

  const handleArchive = async (packId: number) => {
    if (!window.confirm(__('Archiver ce pack de diligences ? Il ne sera plus proposé aux dossiers.'))) {
      return;
    }
    try {
      await vsmeRequest('POST', `diligence-packs/${packId}/archive`);
      void fetchPacks();
    } catch (e) {
      window.alert(errorToUserFacingMessage(e, __('Erreur lors de l\'archivage.')));
    }
  };

  const handleModalSuccess = () => {
    setModal(null);
    void fetchPacks();
  };

  return (
    <>
      <div className="compliance-tab">

        {bootstrap.isCabinetOwner === true && (
          <div className="container-style compliance-tab__section">
            <CabinetPrefillSettingsSection
              cabinetId={cabinetId}
              restBaseUrl={bootstrap.restUrls.accountingHub}
              nonce={bootstrap.nonce}
              onSessionExpired={onSessionExpired}
            />
          </div>
        )}

        <div className="container-style compliance-tab__section" aria-labelledby="mission-policy-heading">
          <FeatureGateNotice decision={missionPolicyEditGate} />
          {missionPolicyState === 'error' && missionPolicyError !== '' ? (
            <p className="compliance-tab__error">{missionPolicyError}</p>
          ) : null}
          <h4 id="mission-policy-heading">{__('Politique de mission')}</h4>
          <p className="compliance-tab__hint">
            {__('Définissez les documents obligatoires et les règles de génération pour vos missions.')}
          </p>
          {missionPolicyState === 'loading' && <p className="compliance-tab__hint">{__('Chargement…')}</p>}
          {missionPolicyState === 'forbidden' && (
            <p className="compliance-tab__hint">{MISSION_POLICY_READ_FORBIDDEN_MESSAGE}</p>
          )}
          {draftMissionPolicy !== null && missionPolicyState !== 'loading' && missionPolicyState !== 'forbidden' && (
            <>
              <div className="permission-row">
                <div className="permission-row__label-wrap">
                  <span className="permission-row__label">{__('Lettre de mission obligatoire')}</span>
                </div>
                <label className="permission-row__select">
                  <input
                    type="checkbox"
                    checked={draftMissionPolicy.require_letter_of_mission}
                    disabled={missionPolicyControlsDisabled}
                    onChange={(e) => setMissionPolicyFlag('require_letter_of_mission', e.target.checked)}
                  />
                </label>
              </div>
              <div className="permission-row">
                <div className="permission-row__label-wrap">
                  <span className="permission-row__label">{__('Questionnaire d’acceptation obligatoire')}</span>
                </div>
                <label className="permission-row__select">
                  <input
                    type="checkbox"
                    checked={draftMissionPolicy.require_acceptance_questionnaire}
                    disabled={missionPolicyControlsDisabled}
                    onChange={(e) => setMissionPolicyFlag('require_acceptance_questionnaire', e.target.checked)}
                  />
                </label>
              </div>
              <div className="permission-row">
                <div className="permission-row__label-wrap">
                  <span className="permission-row__label">{__('Questionnaire LCB-FT obligatoire')}</span>
                </div>
                <label className="permission-row__select">
                  <input
                    type="checkbox"
                    checked={draftMissionPolicy.require_aml_questionnaire}
                    disabled={missionPolicyControlsDisabled}
                    onChange={(e) => setMissionPolicyFlag('require_aml_questionnaire', e.target.checked)}
                  />
                </label>
              </div>
              <div className="permission-row">
                <div className="permission-row__label-wrap">
                  <span className="permission-row__label">{__('Exigence de diligence pour la génération')}</span>
                  <p className="compliance-tab__hint">
                    {__('Contrôle la rigueur requise avant de générer le rapport VS.')}
                  </p>
                </div>
                <div className="permission-row__select">
                  <SimpleSelect
                    options={REPORT_GENERATION_READINESS_OPTIONS()}
                    value={draftMissionPolicy.report_generation_readiness_mode}
                    disabled={missionPolicyControlsDisabled}
                    onChange={(value) => {
                      if (isReadinessModeValue(value)) {
                        setMissionPolicyReadinessMode(value);
                      }
                    }}
                  />
                </div>
              </div>
              {missionPolicyState === 'saving' && <p className="compliance-tab__hint">{__('Enregistrement…')}</p>}
              <div className="role-permission-editor__actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => { void saveMissionPolicy(); }}
                  disabled={missionPolicyControlsDisabled || !missionPolicyDirty}
                >
                  {__('Enregistrer la politique')}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="container-style compliance-tab__section" aria-labelledby="dossier-attribution-heading">
          <FeatureGateNotice decision={dossierAttributionEditGate} />
          {settingsState === 'error' && settingsError !== '' ? (
            <p className="compliance-tab__error">{settingsError}</p>
          ) : null}
          <h4 id="dossier-attribution-heading">{__('Attribution des dossiers clients')}</h4>
          <p className="compliance-tab__hint">
            {__('Choisissez le comportement appliqué aux utilisateurs sans attribution explicite.')}
          </p>
          {settingsState === 'loading' && <p className="compliance-tab__hint">{__('Chargement…')}</p>}
          {settingsState === 'forbidden' && (
            <p className="compliance-tab__hint">{ATTRIBUTION_READ_FORBIDDEN_MESSAGE}</p>
          )}
          {draftSettings !== null && settingsState !== 'loading' && settingsState !== 'forbidden' && (
            <>
              <div className="permission-row">
                <div className="permission-row__label-wrap">
                  <span className="permission-row__label">{__('Attribution par défaut')}</span>
                  <span className="permission-row__description">
                    {__('Détermine si les nouveaux utilisateurs voient tous les dossiers clients ou uniquement ceux attribués.')}
                  </span>
                </div>
                <div className="permission-row__select">
                  <SimpleSelect
                    options={DEFAULT_SCOPE_OPTIONS()}
                    value={draftSettings.default_scope_mode}
                    disabled={attributionControlsDisabled}
                    onChange={handleDefaultScopeChange}
                  />
                </div>
              </div>
              {draftSettings.default_scope_mode === 'explicit' && (
                <div className="permission-row">
                  <div className="permission-row__label-wrap">
                    <span className="permission-row__label">{__('Attribution aux experts-comptables')}</span>
                    <span className="permission-row__description">
                      {__('Choisissez comment attribuer les dossiers à vos experts-comptables. Si vous modifiez ce paramètre, seul le propriétaire pourra attribuer les dossiers clients aux utilisateurs.')}
                    </span>
                  </div>
                  <div className="permission-row__select">
                    <SimpleSelect
                      options={EXPERT_SCOPE_OPTIONS()}
                      value={draftSettings.role_overrides.expert_comptable.mode}
                      disabled={attributionControlsDisabled}
                      onChange={handleExpertScopeChange}
                    />
                  </div>
                </div>
              )}
              {manualModePending && (
                <p className="compliance-tab__hint">{MANUAL_SCOPE_WARNING}</p>
              )}
              {settingsState === 'saving' && <p className="compliance-tab__hint">{__('Enregistrement…')}</p>}
              <div className="role-permission-editor__actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => { void saveSettings(); }}
                  disabled={
                    !canEditAttribution
                    || !dossierAttributionEditGate.allowed
                    || settingsState === 'saving'
                    || !isDirty
                    || manualModePending
                  }
                >
                  {__('Enregistrer')}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="container-style compliance-tab__section" aria-labelledby="compliance-packs-heading">
          <FeatureGateNotice decision={diligencePacksEditGate} />
          {loadState === 'error' && loadError !== '' ? (
            <p className="compliance-tab__error">{loadError}</p>
          ) : null}
          <h4 id="compliance-packs-heading">{__('Packs de diligences')}</h4>
          <p className="compliance-tab__hint">
            {__('Les packs de diligences définissent les contrôles à réaliser pour vos dossiers.')}
          </p>
          {loadState === 'loading' && <p className="compliance-tab__hint">{__('Chargement…')}</p>}
          {loadState === 'forbidden' && (
            <p className="compliance-tab__hint">
              {packsForbiddenDetail !== '' ? packsForbiddenDetail : PACKS_ACCESS_FORBIDDEN_FALLBACK}
            </p>
          )}
          {loadState === 'ready' && (
            <div className="compliance-tab__packs-grid">
              {packs.map((pack) => (
                <DiligencePackCard
                  key={pack.id}
                  pack={pack}
                  disabled={!diligencePacksEditGate.allowed}
                  disabledReason={diligencePacksEditGate.message}
                  onEdit={() => setModal({ kind: 'edit', pack })}
                  onArchive={() => { void handleArchive(pack.id); }}
                />
              ))}
              <button
                type="button"
                className="container-style pack-card pack-card--add"
                onClick={() => setModal({ kind: 'create' })}
                aria-label={__('Créer un pack de diligences')}
                disabled={!diligencePacksEditGate.allowed}
                title={!diligencePacksEditGate.allowed ? diligencePacksEditGate.message : undefined}
              >
                <AddItemIcon className="pack-card__add-icon" />
              </button>
            </div>
          )}
        </div>
      </div>

      {manualWarningOpen && (
        <div className="thn-modal-overlay" role="presentation" onClick={() => setManualWarningOpen(false)}>
          <div
            className="thn-modal thn-modal--small"
            role="dialog"
            aria-modal="true"
            aria-labelledby="manual-attribution-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="thn-modal__header">
              <h3 id="manual-attribution-modal-title">{__('Attribution manuelle')}</h3>
            </div>
            <div className="thn-modal__body">
              <p>{MANUAL_SCOPE_WARNING}</p>
            </div>
            <div className="thn-modal__footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setManualWarningOpen(false);
                  setManualWarningAcknowledged(false);
                  if (settings !== null) {
                    setDraftSettings(settings);
                  }
                }}
              >
                {__('Annuler')}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  setManualWarningAcknowledged(true);
                  setManualWarningOpen(false);
                }}
              >
                {__('Compris')}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal !== null && diligencePacksEditGate.allowed && (
        <DiligencePackEditorModal
          mode={modal}
          cabinetId={cabinetId}
          bootstrapKey={bootstrapKey}
          onClose={() => setModal(null)}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
};

export default CabinetComplianceTab;
